<template>
  <v-card class="AuditCard my-5" text>
    <v-card-title class="px-12" style="display: flex; align-items: center; justify-content: space-between;">
      <span>{{deviceData.name}}</span>
      <!-- Pluviometric chart: Share button + three-dots menu with Download -->
      <div
        v-if="selectedAuditType === 'pluviometricChart' && pluviometricChartData && pluviometricChartData.file && pluviometricChartData.file.url"
        style="display: flex; align-items: center; gap: 8px;"
      >
        <v-btn
          v-if="pluviometricChartData && pluviometricChartData.sharingUrl"
          color="#0098E1"
          class="white--text"
          @click="sharePluviometricChart"
          depressed
          small
        >
          <v-icon left small>mdi-share</v-icon>
          Compartir
        </v-btn>
        <v-menu
          offset-y
          left
          nudge-left="8"
          content-class="pluviometric-download-menu"
          transition="scale-transition"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="black"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-btn
            class="rounded-pill pluviometric-download-btn"
            depressed
            @click="downloadPdf('pluviometricChart')"
          >
            <v-icon left small>mdi-download</v-icon>
            Descargar
          </v-btn>
        </v-menu>
      </div>
      <!-- Other audit types: single download button -->
      <v-btn
        v-else-if="
          (selectedAuditType === 'audit' && auditData && auditData.pdfFile && auditData.pdfFile.url) ||
          (selectedAuditType === 'flowMeasurement' && flowMeasurementData && flowMeasurementData.pdfFile && flowMeasurementData.pdfFile.url) ||
          (selectedAuditType === 'uniformity' && uniformityData && uniformityData.pdfFile && uniformityData.pdfFile.url)"
        icon
        @click="downloadPdf(selectedAuditType)"
        color="black"
      >
        <v-icon>mdi-download</v-icon>
      </v-btn>
    </v-card-title>
    <v-row row no-wrap class="EquipmentSummary px-12">
      <v-col cols="3" class="EquipmentItem EquipmentAuditType pa-0">
        <v-radio-group
          v-model="selectedAuditType"
          mandatory
        >
          <v-radio
            v-for="auditType in filteredAuditTypes"
            :key="auditType.value"
            :label="auditType.label"
            :value="auditType.value"
            color="#0098E1"
            :style="{'font-weight': selectedAuditType === auditType ? '600' : '400'}"
          >
            <template v-slot:label>
              <div>{{auditType.label}}<v-icon v-if="(auditType.value === 'audit' && auditData && auditData.isPublished === false) || (auditType.value === 'flowMeasurement' && flowMeasurementData && flowMeasurementData.isPublished === false) || (auditType.value === 'pluviometricChart' && pluviometricChartData && pluviometricChartData.isPublished === false) || (auditType.value === 'uniformity' && uniformityData && uniformityData.isPublished === false)" class="icon-center" small color="warning">priority_high</v-icon></div>
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>

      <template v-if="selectedAuditType === 'audit' && auditData">
        <v-col
          xs3
          class="EquipmentItem EquipmentItemMetricFlex text-center pa-0"
        >
          <p class="subheading px-2">Caudal de pluviometría relevada</p>
          <div style="flex: 1; align-content: center;">
            <div class="my-3 py-3">
              <p class="display-1 ma-0 mb-1 d-inline" style="color: #00202F">{{auditData.calculatedData ? formatNumber(auditData.calculatedData.totalFlow) : '-'}}</p>
              <p class="unit-text ma-0 d-inline" style="color: #00202F">m3/h</p>
            </div>
          </div>
        </v-col>
        <v-col
          xs3
          class="EquipmentItem text-center pa-0"
        >
          <p class="subheading px-2">Uniformidad de pluviometría relevada</p>
          <div style="flex: 1; align-content: center;">
            <v-progress-circular
              :rotate="-90"
              :size="120"
              :width="5"
              :value="auditData.calculatedData && auditData.calculatedData.pluviometricUniformity || 0"
              color="#0098E1"
              class="ItemValue"
            >
              <p class="display-1 ma-0" style="color: #00202F">{{auditData.calculatedData ? `${formatNumber(auditData.calculatedData.pluviometricUniformity)}%` : '-'}}</p>
            </v-progress-circular>
          </div>
        </v-col>
        <v-col
          xs3
          class="EquipmentItem text-center pa-0"
        >
          <p class="subheading px-2">Bajantes con configuración correcta</p>

          <div style="flex: 1; align-content: center;">
            <v-progress-circular
              :rotate="-90"
              :size="120"
              :width="5"
              :value="outletsConfigurationAnalysis.percentage"
              color="#0098E1"
              class="ItemValue"
            >
              <p class="display-1 ma-0" style="color: #00202F">{{formatNumber(outletsConfigurationAnalysis.percentage)}}%</p>
              <p
                :class="{caption: true, 'ma-0': true}"
                style="color: #00202F"
              >
                {{outletsConfigurationAnalysis.correct}} de {{outletsConfigurationAnalysis.total}}
              </p>
            </v-progress-circular>
          </div>
        </v-col>
      </template>
      <template v-else-if="selectedAuditType === 'flowMeasurement' && flowMeasurementData">
        <v-col
          cols="9"
          class="EquipmentItem EquipmentItemMetricFlex text-center pa-0"
        >
          <p class="subheading px-2">Caudal medido</p>
          <div class="mb-3 pb-3">
            <p class="display-1 ma-0 mb-1 d-inline" style="color: #00202F">{{formatNumber(flowMeasurementAverageFlow)}}</p>
            <p class="unit-text ma-0 d-inline" style="color: #00202F">m3/h</p>
          </div>
        </v-col>
      </template>
      <v-col v-else-if="selectedAuditType === 'pluviometricChart' && pluviometricChartData && showPluviometricChart" cols="9" >
        <PluviometricChart
          :key="pluviometricChartInstanceKey"
          :pluviometricChartData="pluviometricChartData"
        />
      </v-col>
      <v-col v-else-if="selectedAuditType === 'pluviometricChart' && pluviometricChartData && !showPluviometricChart" cols="9" class="d-flex align-center justify-center" style="min-height: 200px;">
        <v-progress-circular indeterminate color="#0098E1" />
      </v-col>
      <template v-else-if="selectedAuditType === 'uniformity' && uniformityData">
        <v-col
          xs6
          class="EquipmentItem EquipmentItemMetricFlex text-center pa-0"
        >
          <p class="subheading px-2">Coeficiente de uniformidad</p>
          <div class="mb-3 pb-3">
            <p class="display-1 ma-0 mb-1 d-inline" :style="{ color: getUniformityColor(uniformityData.coefficientOfUniformity) }">{{uniformityData.coefficientOfUniformity ? `${formatNumber(uniformityData.coefficientOfUniformity)}%` : '-'}}</p>
          </div>
        </v-col>
        <v-col
          xs6
          class="EquipmentItem EquipmentItemMetricFlex text-center pa-0"
          style="border-left: none !important;"
        >
          <p class="subheading px-2">Desviación contra configuración</p>
          <div class="mb-3 pb-3" style="display: flex; align-items: center; justify-content: center;">
            <v-icon v-if="uniformityData.deviationAgainstConfiguration !== null && uniformityData.deviationAgainstConfiguration !== undefined" :color="getDeviationIconColor(uniformityData.deviationAgainstConfiguration)" class="mr-1">{{getDeviationIcon(uniformityData.deviationAgainstConfiguration)}}</v-icon>
            <p class="display-1 ma-0 mb-1 d-inline" style="color: #00202F">{{uniformityData.deviationAgainstConfiguration !== null && uniformityData.deviationAgainstConfiguration !== undefined ? `${formatDeviation(uniformityData.deviationAgainstConfiguration)}%` : '-'}}</p>
          </div>
        </v-col>
      </template>
    </v-row>
    <CardExpansionPanel
      v-if="['audit', 'flowMeasurement', 'uniformity'].includes(selectedAuditType)"
      :auditType="selectedAuditType"
      :auditData="auditData"
      :flowMeasurementData="flowMeasurementData"
      :uniformityData="uniformityData"
    />
  </v-card>
</template>

<script>
import CardExpansionPanel from './CardExpansionPanel'
// import { trackEvent } from '../../lib/track-events'
// import { EVENTS_TO_TRACK } from '../../constants/const'
import { getOutletsConfigurationAnalysis, formatNumberHelper } from '../../lib/helpers'
import PluviometricChart from './PluviometricChart'
export default {
  data () {
    return {
      auditTypes: [
        {
          label: 'Relevamiento',
          value: 'audit'
        },
        {
          label: 'Medición de caudal',
          value: 'flowMeasurement'
        },
        {
          label: 'Cartilla',
          value: 'pluviometricChart'
        },
        {
          label: 'Uniformidad',
          value: 'uniformity'
        }
      ],
      selectedAuditType: 'audit',
      outletsConfigurationAnalysis: {
        percentage: 0,
        total: 0,
        correct: 0
      },
      flowMeasurementAverageFlow: 0,
      pluviometricChartInstanceKey: 0,
      showPluviometricChart: false,
      pluviometricChartDelayTimer: null
    }
  },
  props: ['auditData', 'deviceData', 'flowMeasurementData', 'pluviometricChartData', 'uniformityData'],
  beforeDestroy () {
    if (this.pluviometricChartDelayTimer) {
      clearTimeout(this.pluviometricChartDelayTimer)
    }
  },
  components: {
    CardExpansionPanel,
    PluviometricChart,
  },
  methods: {
    formatNumber (num, decimals = 1) {
      return formatNumberHelper(num, decimals)
    },
    downloadPdf () {
      if (this.selectedAuditType === 'pluviometricChart') {
        window.open(this.pluviometricChartData.file.url, '_blank')
      } else if (this.selectedAuditType === 'flowMeasurement') {
        window.open(this.flowMeasurementData.pdfFile.url, '_blank')
      } else if (this.selectedAuditType === 'audit') {
        window.open(this.auditData.pdfFile.url, '_blank')
      } else if (this.selectedAuditType === 'uniformity') {
        window.open(this.uniformityData.pdfFile.url, '_blank')
      }
    },
    sharePluviometricChart () {
      const url = this.pluviometricChartData && this.pluviometricChartData.sharingUrl
      if (!url) return
      const text = `Te quiero compartir esta carta de riego elaborada por PONCE. Accede mediante este link: ${url}`
      const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`
      window.open(waUrl, '_blank')
    },
    getUniformityColor (value) {
      if (value === null || value === undefined) return '#00202F'
      const numValue = parseFloat(value)
      if (numValue > 90) return '#A5B948' // Excellent
      if (numValue >= 85 && numValue <= 90) return '#FFD400' // Acceptable
      if (numValue >= 80 && numValue < 85) return '#FF7A00' // Fair
      return '#F93A2F' // Poor (< 80)
    },
    getDeviationIcon (value) {
      if (value === null || value === undefined) return ''
      const numValue = parseFloat(value)
      if (numValue > 0) return 'mdi-arrow-up'
      if (numValue < 0) return 'mdi-arrow-down'
      return ''
    },
    getDeviationIconColor (value) {
      if (value === null || value === undefined) return '#00202F'
      const numValue = parseFloat(value)
      if (numValue > 0) return '#00202F'
      if (numValue < 0) return '#00202F'
      return '#00202F'
    },
    formatDeviation (value) {
      if (value === null || value === undefined) return '-'
      const numValue = parseFloat(value)
      return this.formatNumber(Math.abs(numValue))
    }
  },
  computed: {
    filteredAuditTypes () {
      return this.auditTypes.filter(auditType => this.deviceData[`${auditType.value}s`].length > 0)
    }
  },
  watch: {
    selectedAuditType (newVal) {
      if (newVal === 'pluviometricChart') {
        this.showPluviometricChart = false
        if (this.pluviometricChartDelayTimer) {
          clearTimeout(this.pluviometricChartDelayTimer)
        }
        this.pluviometricChartInstanceKey = Date.now()
        this.pluviometricChartDelayTimer = setTimeout(() => {
          this.showPluviometricChart = true
          this.pluviometricChartDelayTimer = null
        }, 250)
      } else {
        this.showPluviometricChart = false
        if (this.pluviometricChartDelayTimer) {
          clearTimeout(this.pluviometricChartDelayTimer)
          this.pluviometricChartDelayTimer = null
        }
      }
    },
    auditData: {
      handler (newVal) {
        if (newVal) {
          this.outletsConfigurationAnalysis = getOutletsConfigurationAnalysis(newVal)
        } else {
          this.outletsConfigurationAnalysis = {
            percentage: 0,
            total: 0,
            correct: 0
          }
        }
      },
      deep: true,
      immediate: true
    },
    flowMeasurementData: {
      handler (newVal) {
        if (newVal && newVal.flowSamples && Array.isArray(newVal.flowSamples) && newVal.flowSamples.length > 0) {
          this.flowMeasurementAverageFlow = newVal.flowSamples.reduce((acc, sample) => acc + parseFloat(sample.flow || 0), 0) / newVal.flowSamples.length
        } else {
          this.flowMeasurementAverageFlow = 0
        }
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style>
.pluviometric-download-menu {
  background: transparent !important;
  box-shadow: none !important;
  padding: 10px !important;
  border: none !important;
  transform: translate(10px, 0) !important;
}
.pluviometric-download-menu .pluviometric-download-btn {
  background-color: #fff !important;
  color: #616161 !important;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.3);
}
</style>


// WEBPACK FOOTER //
// MainCard.vue?0a1b2f02